import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Shield, Truck, Eye, EyeOff, X,
  CheckCircle2, ArrowRight, Loader2, LogOut, Settings,
  Zap, Star, MessageCircle, Send, Paperclip,
  TrendingUp, FileCheck, AlertCircle, Lock,
  ChevronLeft, ChevronRight, Award, Flame, Trophy,
  Calendar, DollarSign, FileText, Trash2, ExternalLink,
  Search, ChevronDown, Upload, Phone, Share2, Camera, Smile, ArrowLeft,
  Check, CheckCircle, XCircle, ClipboardList,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

import { ChatRoom } from "./ChatRoom";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";
const MID    = "#1C1C1E";
const DRIVER_COLOR = "#4ADE80";

const HUB_META = {
  carrier:   { icon: Building2, label: "Carriers",   accent: "#FF4D00", glow: "255,77,0",    desc: "Bid on drivers",      badge: "HIRING",     badgeBg: "rgba(255,77,0,0.15)" },
  recruiter: { icon: Users,     label: "Recruiters", accent: "#F5C842", glow: "245,200,66",  desc: "Manage pipeline",     badge: "TALENT",     badgeBg: "rgba(245,200,66,0.15)" },
  safety:    { icon: Shield,    label: "Safety",     accent: "#38BDF8", glow: "56,189,248",  desc: "Vet & approve",       badge: "COMPLIANCE", badgeBg: "rgba(56,189,248,0.15)" },
  driver:    { icon: Truck,     label: "Drivers",    accent: "#4ADE80", glow: "74,222,128",  desc: "Your market value",   badge: "PROFILES",   badgeBg: "rgba(74,222,128,0.15)" },
  admin:     { icon: Star,      label: "Admin",      accent: "#A78BFA", glow: "167,139,250", desc: "Full access",         badge: "SYSTEM",     badgeBg: "rgba(167,139,250,0.15)" },
};

// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// ── DRIVERS HUB ───────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function DriversHub({ token, apiBase }) {
  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState({});
  const [datesForm, setDatesForm]   = useState({ cdl_issue_date: "", drug_screen_eligible_date: "", start_eligible_date: "" });
  const [savingDates, setSavingDates] = useState(false);
  const [seenCard, setSeenCard]     = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm]     = useState({ state: "", equipment_preference: "", availability: "", cdl_class: "", years_experience: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [driverTab, setDriverTab]   = useState("profile");
  const [myBids, setMyBids]           = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [referrals, setReferrals]     = useState({ referrals: [], pending_earnings: 0, paid_earnings: 0, driver_id: "" });
  const fileRefs = { mvr: useRef(), psp: useRef(), clearinghouse: useRef(), previous_cdl: useRef() };
  const cdlSlotRefs = { cdl_back: useRef(), previous_cdl_1: useRef(), previous_cdl_2: useRef() };
  const emptyEmpEntry = { employer: "", title: "", from_date: "", to_date: "" };
  const [supplementalHistory, setSupplementalHistory] = useState([emptyEmpEntry, emptyEmpEntry, emptyEmpEntry, emptyEmpEntry]);
  const [supplementalSubmitting, setSupplementalSubmitting] = useState(false);
  const [supplementalDone, setSupplementalDone] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const data = await api.marketDriverProfile(token);
      setProfile(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  // Real-time refresh every 5 seconds
  useEffect(() => {
    const id = setInterval(loadProfile, 5000);
    return () => clearInterval(id);
  }, [loadProfile]);

  // Load referrals when on profile tab (referral section is in profile tab)
  useEffect(() => {
    if (driverTab !== "profile") return;
    api.marketDriverReferrals(token)
      .then(r => setReferrals(r))
      .catch(() => {});
  }, [driverTab, token]);

  // Load received bids when switching to bids tab
  useEffect(() => {
    if (driverTab !== "bids") return;
    setBidsLoading(true);
    api.marketGetMyBids(token)
      .then(r => setMyBids(r.bids || []))
      .catch(() => setMyBids([]))
      .finally(() => setBidsLoading(false));
  }, [driverTab, token]);

  // Sync saved dates from profile into form
  useEffect(() => {
    if (profile?.found) {
      setDatesForm((f) => ({
        cdl_issue_date: profile.cdl_issue_date || f.cdl_issue_date || "",
        drug_screen_eligible_date: profile.drug_screen_eligible_date || f.drug_screen_eligible_date || "",
        start_eligible_date: profile.start_eligible_date || f.start_eligible_date || "",
      }));
      // Sync editable profile fields
      setEditForm((f) => ({
        state:                f.state                || profile.state                     || "",
        equipment_preference: f.equipment_preference || profile.equipment_preference       || "",
        availability:         f.availability         || profile.availability               || "",
        cdl_class:            f.cdl_class             || profile.cdl_class                 || "",
        years_experience:     f.years_experience      || (profile.years_experience != null ? String(profile.years_experience) : ""),
      }));
    }
  }, [profile]);

  async function saveDates(fields) {
    setSavingDates(true);
    try {
      await api.marketDriverSetDates(token, fields);
      toast.success("Saved");
      await loadProfile();
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally { setSavingDates(false); }
  }

  async function handleSaveProfile() {
    setSavingProfile(true);
    try {
      const payload = {};
      if (editForm.state)              payload.state = editForm.state;
      if (editForm.equipment_preference) payload.equipment_preference = editForm.equipment_preference;
      if (editForm.availability)       payload.availability = editForm.availability;
      if (editForm.cdl_class)          payload.cdl_class = editForm.cdl_class;
      if (editForm.years_experience)   payload.years_experience = parseInt(editForm.years_experience, 10);
      await api.marketDriverUpdateProfile(token, payload);
      toast.success("Profile updated");
      await loadProfile();
      setShowEditProfile(false);
    } catch { toast.error("Failed to update profile."); }
    finally { setSavingProfile(false); }
  }

  async function uploadDoc(docType, file) {
    setUploading((u) => ({ ...u, [docType]: true }));
    try {
      const uploaded = await api.uploadFile(file);
      await api.marketDriverUploadDoc(token, { doc_type: docType, file_path: uploaded.path });
      toast.success(`${docType.toUpperCase()} uploaded successfully`);
      await loadProfile();
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || "Please try again"}`);
    } finally { setUploading((u) => ({ ...u, [docType]: false })); }
  }

  function handleFileChange(docType, e) {
    const file = e.target.files?.[0];
    if (file) uploadDoc(docType, file);
  }

  const DRIVER_GLOW  = "74,222,128";

  const DOC_CARDS = [
    { key: "mvr",           label: "MVR",             desc: "Motor Vehicle Record",        has: profile?.has_mvr },
    { key: "psp",           label: "PSP",             desc: "Pre-employment Screen. Prog.", has: profile?.has_psp },
    { key: "clearinghouse", label: "Clearinghouse",   desc: "Drug & Alcohol Clearinghouse", has: profile?.has_clearinghouse },
    ...(profile?.requires_previous_cdl
      ? [{ key: "previous_cdl", label: "Previous CDL", desc: "Required — CDL issued < 2 yrs ago", has: profile?.has_previous_cdl }]
      : []),
  ];

  const CDL_SLOTS = [
    { key: "cdl_back",       label: "CDL Back",       desc: "Reverse side of your CDL",           has: profile?.has_cdl_back,       adminOnly: false },
    { key: "previous_cdl_1", label: "Previous CDL 1", desc: "Prior CDL (most recent)",             has: profile?.has_previous_cdl_1, adminOnly: false },
    { key: "previous_cdl_2", label: "Previous CDL 2", desc: "Prior CDL (older / state transfer)",  has: profile?.has_previous_cdl_2, adminOnly: false },
  ];

  const mStatusColors = {
    applied:      { color: ACCENT,        bg: "rgba(255,77,0,0.1)",   label: "Applied — Pending Review" },
    approved:     { color: DRIVER_COLOR,  bg: "rgba(74,222,128,0.1)", label: "Approved — Visible on Market" },
    not_approved: { color: "#ef4444",     bg: "rgba(239,68,68,0.1)",  label: "Not Approved" },
    transferred:  { color: GOLD,          bg: "rgba(245,200,66,0.1)", label: "Transferred — Contact Recruiter" },
  };
  const mStatus = mStatusColors[profile?.market_status] || mStatusColors.applied;

  // Best market value: highest bid or weekly royalty
  const bestValue = profile?.highest_bid || 0;
  const bestRoyalty = profile?.best_weekly_royalty || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: DRIVER_COLOR }} />
      </div>
    );
  }

  // ── Onboarding card — shown once per login before the full hub ──
  if (!seenCard && profile?.found) {
    const bonusItems = [
      { label: "$65 — Docs Complete",   status: profile.bonus_docs_status },
      { label: "$15 — Drug Screen Date", status: profile.bonus_drug_screen_status },
      { label: "$20 — Start Date Set",   status: profile.bonus_start_status },
    ];
    const bonusStatusMeta = (s) => {
      if (s === "approved")         return { color: DRIVER_COLOR, bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.3)", label: "Approved" };
      if (s === "pending_approval") return { color: GOLD,         bg: "rgba(245,200,66,0.1)", border: "rgba(245,200,66,0.3)", label: "Pending" };
      return                               { color: "#555",        bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)", label: "Not yet" };
    };
    return (
      <motion.div
        data-testid="driver-onboarding-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="space-y-6"
      >
        {/* Header */}
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Driver Hub</p>
          <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em] text-white">
            Welcome Back
          </h2>
        </div>

        {/* Identity + status */}
        <div
          className="px-5 py-4 flex items-center justify-between flex-wrap gap-4"
          style={{ background: MID, border: `1px solid rgba(${DRIVER_GLOW},0.25)` }}
        >
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-0.5" style={{ color: DRIVER_COLOR }}>
              Your Profile
            </p>
            <h3 className="font-head font-black text-xl uppercase text-white">
              {profile.first_name} {profile.last_name}
            </h3>
            <p className="text-white/40 text-sm mt-0.5">
              {profile.state} · {profile.cdl_class || "CDL-A"} · {profile.position || "Truck Driver"}
            </p>
          </div>
          <div
            className="px-3 py-1.5"
            style={{ background: mStatus.bg, border: `1px solid ${mStatus.color}40`, color: mStatus.color }}
          >
            <p className="font-head text-[9px] font-black uppercase tracking-[0.15em]">{mStatus.label}</p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Bids received */}
          <div
            className="p-5 flex flex-col justify-between"
            style={{ background: `rgba(${DRIVER_GLOW},0.07)`, border: `1px solid rgba(${DRIVER_GLOW},0.25)` }}
          >
            <p className="font-head text-[9px] uppercase tracking-[0.3em] text-white/40 mb-2">Live Bids Received</p>
            <p className="font-head font-black text-5xl leading-none" style={{ color: DRIVER_COLOR }}>
              {profile.bid_count ?? 0}
            </p>
            <p className="text-[10px] text-white/30 mt-2">
              {(profile.bid_count ?? 0) === 0
                ? "No carrier bids yet — your profile is on the market."
                : `${profile.bid_count} active carrier offer${profile.bid_count !== 1 ? "s" : ""} on your profile.`}
            </p>
          </div>

          {/* Bonus summary */}
          <div
            className="p-5 flex flex-col justify-between"
            style={{ background: "rgba(245,200,66,0.06)", border: "1px solid rgba(245,200,66,0.22)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="font-head text-[9px] uppercase tracking-[0.3em] text-white/40">Bonus Tracker</p>
              <span
                className="font-head text-[10px] font-black px-2 py-0.5"
                style={{ background: "rgba(245,200,66,0.15)", color: GOLD, border: "1px solid rgba(245,200,66,0.3)" }}
              >
                ${profile.bonus_total ?? 0} earned
              </span>
            </div>
            <div className="space-y-2">
              {bonusItems.map(({ label, status }) => {
                const m = bonusStatusMeta(status);
                return (
                  <div
                    key={label}
                    className="flex items-center justify-between px-3 py-2"
                    style={{ background: m.bg, border: `1px solid ${m.border}` }}
                  >
                    <p className="font-head text-[10px] uppercase tracking-[0.1em] text-white/70">{label}</p>
                    <span className="font-head text-[9px] uppercase px-1.5 py-0.5" style={{ color: m.color, border: `1px solid ${m.border}` }}>
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex justify-end">
          <motion.button
            data-testid="driver-enter-hub-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setSeenCard(true)}
            className="flex items-center gap-2 px-8 py-3 font-head text-sm font-black uppercase tracking-[0.2em] transition-all hover:brightness-110"
            style={{ background: DRIVER_COLOR, color: DARK }}
          >
            Enter My Hub <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>

        {/* ── Drivers Global Chat — visible immediately on login ── */}
        <div>
          <div className="mb-4">
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Drivers Lounge</p>
            <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
              Drivers Global Chat
            </h3>
            <p className="text-white/30 text-xs mt-1">
              Open to all drivers on the platform. No self-promotion or poaching — violations result in a permanent ban.
            </p>
          </div>
          <ChatRoom room="drivers_only" roomLabel="Drivers · Global" token={token} hub="driver" />
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section header + back button */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Drivers Hub</p>
          <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em] text-white">
            Your Market Profile
          </h2>
        </div>
        <button
          data-testid="driver-back-to-hub-home"
          onClick={() => setSeenCard(false)}
          className="flex items-center gap-2.5 font-head text-xs font-bold uppercase tracking-[0.18em] px-5 py-3 transition-all hover:brightness-110 active:scale-95"
          style={{
            border: `1px solid rgba(74,222,128,0.3)`,
            color: DRIVER_COLOR,
            background: `rgba(74,222,128,0.06)`,
          }}
        >
          <ArrowLeft
            className="w-4 h-4"
            style={{ animation: "hubArrow 1.4s ease-in-out infinite", color: DRIVER_COLOR }}
          />
          Hub Home
        </button>
      </div>

      {!profile?.found ? (
        /* ── No profile found ── */
        <div
          className="flex flex-col items-center py-16 text-center"
          style={{
            background: MID,
            border: `1px solid rgba(${DRIVER_GLOW},0.15)`,
          }}
        >
          <div
            className="w-16 h-16 flex items-center justify-center mb-4"
            style={{ background: `rgba(${DRIVER_GLOW},0.08)`, border: `1px solid rgba(${DRIVER_GLOW},0.2)` }}
          >
            <Truck className="w-8 h-8" style={{ color: DRIVER_COLOR }} />
          </div>
          <p className="font-head font-black text-base uppercase tracking-[0.08em] text-white mb-2">
            No Application Found
          </p>
          <p className="text-white/40 text-sm max-w-sm mx-auto leading-relaxed">
            {profile?.message || "We couldn't match your phone number to an application. Please contact a recruiter."}
          </p>
          <a
            href="/apply"
            className="mt-6 font-head text-xs font-black uppercase tracking-[0.25em] px-6 py-3 transition-all hover:brightness-110"
            style={{ background: DRIVER_COLOR, color: DARK }}
          >
            Apply Now →
          </a>
        </div>
      ) : (
        <>
          {/* ── Profile card + market value ── */}
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Identity */}
            <div
              className="sm:col-span-2 p-5"
              style={{ background: MID, border: `1px solid rgba(${DRIVER_GLOW},0.2)` }}
            >
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-1" style={{ color: DRIVER_COLOR }}>
                    Your Profile
                  </p>
                  <h3 className="font-head font-black text-xl uppercase text-white">
                    {profile.first_name} {profile.last_name}
                  </h3>
                  <p className="text-white/50 text-sm mt-0.5">
                    {profile.state} · {profile.cdl_class || "CDL-A"} · {profile.position || "Truck Driver"}
                  </p>
                </div>
                <div
                  className="px-3 py-1.5"
                  style={{ background: mStatus.bg, border: `1px solid ${mStatus.color}40`, color: mStatus.color }}
                >
                  <p className="font-head text-[9px] font-black uppercase tracking-[0.15em]">{mStatus.label}</p>
                </div>
              </div>
            </div>

            {/* Market value stats */}
            <div className="flex flex-col gap-3">
              {/* Best bid offer */}
              <div
                className="flex-1 p-4 flex flex-col justify-center"
                style={{ background: `rgba(${DRIVER_GLOW},0.08)`, border: `1px solid rgba(${DRIVER_GLOW},0.25)` }}
              >
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">
                  {bestValue ? "Best Offer" : "Total Live Bids"}
                </p>
                <p className="font-head font-black text-3xl leading-none" style={{ color: DRIVER_COLOR }}>
                  {bestValue ? `$${bestValue.toLocaleString()}` : (profile.bid_count ?? 0)}
                </p>
                {bestValue > 0 && (
                  <p className="text-[10px] text-white/30 mt-1">highest placement fee from {profile.bid_count} bid{profile.bid_count !== 1 ? "s" : ""}</p>
                )}
              </div>

              {/* Weekly royalty (Stage 5) */}
              {bestRoyalty && (
                <div
                  className="flex-1 p-4 flex flex-col justify-center"
                  style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.25)" }}
                >
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Best Weekly Royalty</p>
                  <p className="font-head font-black text-2xl leading-none" style={{ color: GOLD }}>
                    ${bestRoyalty.toFixed(2)}<span className="text-base font-semibold">/wk</span>
                  </p>
                  <p className="text-[10px] text-white/30 mt-1">10% of carrier's Weekly Flex plan</p>
                </div>
              )}
              {!bestRoyalty && (
                <div
                  className="flex-1 p-4 flex flex-col justify-center"
                  style={{ background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.12)" }}
                >
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Weekly Royalty</p>
                  <p className="font-head font-black text-2xl" style={{ color: "rgba(245,200,66,0.3)" }}>—</p>
                  <p className="text-[10px] text-white/25 mt-1">No Weekly Flex bids yet</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Tab switcher ── */}
          <div className="flex gap-2 mb-5 flex-wrap">
            {[
              { key: "profile", label: "My Profile" },
              { key: "bids",    label: `Bids Received${myBids.length > 0 ? ` (${myBids.length})` : ""}` },
              ...(profile?.dot_history_requested && !profile?.dot_history_submitted ? [{
                key: "dot_history",
                label: "10-Year DOT History",
                alert: true,
              }] : []),
            ].map(({ key, label, alert }) => (
              <button
                key={key}
                data-testid={`driver-tab-${key}`}
                onClick={() => setDriverTab(key)}
                className="relative font-head text-xs uppercase tracking-[0.15em] px-4 py-2 transition-all"
                style={{
                  background: driverTab === key ? (alert ? "rgba(245,200,66,0.12)" : "rgba(74,222,128,0.1)") : "transparent",
                  border: `1px solid ${driverTab === key ? (alert ? "rgba(245,200,66,0.5)" : "rgba(74,222,128,0.4)") : "rgba(255,255,255,0.1)"}`,
                  color: driverTab === key ? (alert ? "#F5C842" : DRIVER_COLOR) : "rgba(255,255,255,0.35)",
                }}
              >
                {label}
                {alert && (
                  <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                    style={{ background: "#F5C842", fontSize: "8px", color: "#0A0A0A", fontWeight: "900" }}>!</span>
                )}
              </button>
            ))}
          </div>

          {/* ── Bids Received tab ── */}
          {driverTab === "bids" && (
            <div data-testid="driver-bids-received-section" className="space-y-4">
              <div className="mb-2">
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Offers</p>
                <h2 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: DRIVER_COLOR }}>
                  Bids on Your Profile
                </h2>
                <p className="text-xs text-white/30 mt-1">Carrier identity is kept confidential. When Hylas selects a match, we'll reach out to coordinate your placement.</p>
              </div>
              {bidsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-green-400 rounded-full animate-spin" />
                </div>
              ) : myBids.length === 0 ? (
                <div className="flex flex-col items-center py-16 text-center" style={{ background: MID, border: "1px solid rgba(74,222,128,0.08)" }}>
                  <p className="font-head text-sm text-white/25 uppercase tracking-[0.2em] mb-1">No bids yet</p>
                  <p className="text-xs text-white/20">Once carriers place bids on your profile, they'll appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myBids.map((bid, i) => {
                    const prog = bid.pay_program?.replace(/_/g, " ") || "—";
                    const equip = bid.equipment_package || "—";
                    const date  = bid.created_at ? new Date(bid.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
                    return (
                      <div
                        key={bid.id || i}
                        data-testid={`driver-received-bid-${i}`}
                        className="px-5 py-4 space-y-3"
                        style={{ background: MID, border: "1px solid rgba(74,222,128,0.12)" }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/30 mb-0.5">Offer Received</p>
                            <p className="font-head font-black text-lg text-white">{prog} · {equip}</p>
                          </div>
                          {date && (
                            <span className="font-head text-[10px] text-white/30 shrink-0">{date}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs">
                          {bid.driver_pay_cpm && (
                            <div>
                              <p className="text-white/30 mb-0.5 font-head text-[9px] uppercase tracking-[0.15em]">CPM Rate</p>
                              <p className="font-head font-bold text-white">{bid.driver_pay_cpm}¢/mile</p>
                            </div>
                          )}
                          {bid.driver_pay_percentage && (
                            <div>
                              <p className="text-white/30 mb-0.5 font-head text-[9px] uppercase tracking-[0.15em]">Revenue Share</p>
                              <p className="font-head font-bold text-white">{bid.driver_pay_percentage}%</p>
                            </div>
                          )}
                          {bid.driver_pay_package && bid.driver_pay_package !== bid.equipment_package && (
                            <div>
                              <p className="text-white/30 mb-0.5 font-head text-[9px] uppercase tracking-[0.15em]">Pay Package</p>
                              <p className="font-head font-bold text-white">{bid.driver_pay_package}</p>
                            </div>
                          )}
                        </div>
                        {bid.bid_message && (
                          <div className="px-3 py-2.5 space-y-1.5" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.12)" }}>
                            <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/30 mb-2">Carrier Offer Details</p>
                            {bid.bid_message.split("\n").map((line, i) => {
                              const [labelPart, ...rest] = line.split(":");
                              const val = rest.join(":").trim();
                              return val ? (
                                <div key={i} className="flex items-baseline justify-between gap-3">
                                  <span className="font-head text-[9px] uppercase tracking-[0.12em] text-white/35 shrink-0">{labelPart.trim()}</span>
                                  <span className="text-xs font-bold text-white text-right">{val}</span>
                                </div>
                              ) : (
                                <p key={i} className="text-xs text-white/60">{line}</p>
                              );
                            })}
                          </div>
                        )}
                        <p className="text-[10px] text-white/20 italic">Carrier identity is anonymous. Hylas will coordinate your placement when selected.</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 10-Year DOT History tab ── */}
          {driverTab === "dot_history" && (
            <div data-testid="driver-dot-history-section" className="space-y-4">
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">FMCSA §391.21 Compliance</p>
                <h2 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: "#F5C842" }}>
                  10-Year Employment History
                </h2>
                <p className="text-xs text-white/40 mt-1 leading-relaxed">
                  A carrier has requested your complete 10-year DOT employment history. Your initial application covered 3 years.
                  Please add your remaining employers below (up to 4 additional). Only commercial driving and safety-sensitive positions are required.
                </p>
              </div>

              {supplementalDone || profile?.dot_history_submitted ? (
                <div className="flex flex-col items-center py-12 text-center" style={{ background: MID, border: "1px solid rgba(34,197,94,0.2)" }}>
                  <CheckCircle2 className="w-10 h-10 mb-3" style={{ color: "#22C55E" }} />
                  <p className="font-head text-sm text-white uppercase tracking-[0.2em]">Submitted</p>
                  <p className="text-xs text-white/30 mt-1">Your carrier has been notified and will update your DQ file.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {supplementalHistory.map((emp, idx) => (
                    <div key={idx} className="p-4 space-y-3" style={{ background: MID, border: "1px solid rgba(245,200,66,0.1)" }}>
                      <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30">Employer {idx + 1}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40 mb-1">Company Name</p>
                          <input
                            data-testid={`supplemental-employer-${idx}`}
                            type="text"
                            value={emp.employer}
                            onChange={(e) => {
                              const updated = [...supplementalHistory];
                              updated[idx] = { ...updated[idx], employer: e.target.value };
                              setSupplementalHistory(updated);
                            }}
                            className="w-full bg-transparent border-b border-white/20 py-1.5 text-sm text-white placeholder-white/20 outline-none focus:border-yellow-400/50 transition-colors"
                            placeholder="Employer name…"
                          />
                        </div>
                        <div>
                          <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40 mb-1">Title / Position</p>
                          <input
                            data-testid={`supplemental-title-${idx}`}
                            type="text"
                            value={emp.title}
                            onChange={(e) => {
                              const updated = [...supplementalHistory];
                              updated[idx] = { ...updated[idx], title: e.target.value };
                              setSupplementalHistory(updated);
                            }}
                            className="w-full bg-transparent border-b border-white/20 py-1.5 text-sm text-white placeholder-white/20 outline-none focus:border-yellow-400/50 transition-colors"
                            placeholder="CDL-A Driver…"
                          />
                        </div>
                        <div>
                          <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40 mb-1">From (MM/YYYY)</p>
                          <input
                            data-testid={`supplemental-from-${idx}`}
                            type="text"
                            value={emp.from_date}
                            onChange={(e) => {
                              const updated = [...supplementalHistory];
                              updated[idx] = { ...updated[idx], from_date: e.target.value };
                              setSupplementalHistory(updated);
                            }}
                            className="w-full bg-transparent border-b border-white/20 py-1.5 text-sm text-white placeholder-white/20 outline-none focus:border-yellow-400/50 transition-colors"
                            placeholder="01/2016"
                          />
                        </div>
                        <div>
                          <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40 mb-1">To (MM/YYYY)</p>
                          <input
                            data-testid={`supplemental-to-${idx}`}
                            type="text"
                            value={emp.to_date}
                            onChange={(e) => {
                              const updated = [...supplementalHistory];
                              updated[idx] = { ...updated[idx], to_date: e.target.value };
                              setSupplementalHistory(updated);
                            }}
                            className="w-full bg-transparent border-b border-white/20 py-1.5 text-sm text-white placeholder-white/20 outline-none focus:border-yellow-400/50 transition-colors"
                            placeholder="12/2018 or Present"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid="supplemental-submit-btn"
                    disabled={supplementalSubmitting}
                    onClick={async () => {
                      const filled = supplementalHistory.filter((e) => e.employer.trim());
                      if (filled.length === 0) {
                        toast.error("Add at least one employer to submit.");
                        return;
                      }
                      setSupplementalSubmitting(true);
                      try {
                        await api.marketSubmitSupplementalHistory(token, { employment_history_supplemental: supplementalHistory });
                        setSupplementalDone(true);
                        toast.success("10-Year DOT history submitted!");
                        loadProfile();
                      } catch {
                        toast.error("Failed to submit. Please try again.");
                      } finally {
                        setSupplementalSubmitting(false);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 py-4 font-head text-sm font-black uppercase tracking-[0.2em] transition-all hover:brightness-110 disabled:opacity-60"
                    style={{ background: "#F5C842", color: "#0A0A0A" }}
                  >
                    {supplementalSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Submit 10-Year History
                  </button>
                  <p className="text-[10px] text-white/25 text-center">
                    Only list commercial/safety-sensitive positions. Gaps in employment are acceptable — just fill what applies.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Profile tab content ── */}
          {driverTab === "profile" && (<>

          {/* DOT history notification banner (if requested but not yet submitted) */}
          {profile?.dot_history_requested && !profile?.dot_history_submitted && (
            <div
              data-testid="driver-dot-history-alert"
              className="mb-4 px-4 py-3 flex items-start gap-3 cursor-pointer"
              style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.3)" }}
              onClick={() => setDriverTab("dot_history")}
            >
              <ClipboardList className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#F5C842" }} />
              <div>
                <p className="font-head text-xs font-bold uppercase tracking-[0.12em]" style={{ color: "#F5C842" }}>
                  Action Required — 10-Year DOT History
                </p>
                <p className="text-[11px] text-white/50 mt-0.5">
                  {profile.dot_history_requested_by
                    ? `A carrier has requested your 7-year supplemental employment history to complete your DOT qualification.`
                    : `Your carrier has requested your full 10-year DOT employment history.`}
                  {" "}Tap to complete it now →
                </p>
              </div>
            </div>
          )}

          {profile?.dot_history_submitted && (
            <div
              data-testid="driver-dot-history-submitted"
              className="mb-4 px-4 py-3 flex items-center gap-3"
              style={{ background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.2)" }}
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#22C55E" }} />
              <p className="text-xs" style={{ color: "#22C55E" }}>
                10-Year DOT history submitted — your carrier will be notified.
              </p>
            </div>
          )}

          {/* ── Edit Profile ── */}
          <div style={{ background: MID, border: `1px solid rgba(${DRIVER_GLOW},0.2)` }}>
            <button data-testid="driver-edit-profile-toggle"
              onClick={() => setShowEditProfile((v) => !v)}
              className="w-full px-4 py-3 flex items-center gap-3 text-left"
              style={{ borderBottom: showEditProfile ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
              <Settings className="w-4 h-4 shrink-0" style={{ color: DRIVER_COLOR }} />
              <div className="flex-1">
                <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Edit Your Profile</p>
                <p className="text-[10px] text-white/30 mt-0.5">Update state, equipment preference and availability</p>
              </div>
              <ChevronDown className="w-4 h-4 text-white/30 transition-transform" style={{ transform: showEditProfile ? "rotate(180deg)" : "rotate(0deg)" }} />
            </button>
            {showEditProfile && (
              <div className="px-4 py-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* State */}
                  <div>
                    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1.5">State</label>
                    <select data-testid="driver-edit-state" value={editForm.state}
                      onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2.5 text-xs focus:outline-none focus:border-[#4ADE80]">
                      <option value="">— Select state —</option>
                      {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  {/* CDL Class */}
                  <div>
                    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1.5">CDL Class</label>
                    <select data-testid="driver-edit-cdl-class" value={editForm.cdl_class}
                      onChange={(e) => setEditForm((p) => ({ ...p, cdl_class: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2.5 text-xs focus:outline-none focus:border-[#4ADE80]">
                      <option value="">— Select class —</option>
                      <option value="CDL-A">CDL-A</option>
                      <option value="CDL-B">CDL-B</option>
                      <option value="CDL-C">CDL-C</option>
                    </select>
                  </div>
                  {/* Equipment Preference */}
                  <div>
                    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1.5">Equipment Preference</label>
                    <select data-testid="driver-edit-equipment" value={editForm.equipment_preference}
                      onChange={(e) => setEditForm((p) => ({ ...p, equipment_preference: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2.5 text-xs focus:outline-none focus:border-[#4ADE80]">
                      <option value="">— Select preference —</option>
                      <option value="semi_truck">Semi Truck</option>
                      <option value="box_truck">Box Truck</option>
                      <option value="both">Both / Open</option>
                    </select>
                  </div>
                  {/* Availability */}
                  <div>
                    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1.5">Availability</label>
                    <select data-testid="driver-edit-availability" value={editForm.availability}
                      onChange={(e) => setEditForm((p) => ({ ...p, availability: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2.5 text-xs focus:outline-none focus:border-[#4ADE80]">
                      <option value="">— Select availability —</option>
                      <option value="immediately">Immediately Available</option>
                      <option value="2_weeks">2 Weeks Notice</option>
                      <option value="30_days">30 Days Notice</option>
                      <option value="open">Open / Flexible</option>
                    </select>
                  </div>
                  {/* Years Experience */}
                  <div>
                    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1.5">Years of Experience</label>
                    <input data-testid="driver-edit-years-exp" type="number" min="0" max="50"
                      value={editForm.years_experience}
                      onChange={(e) => setEditForm((p) => ({ ...p, years_experience: e.target.value }))}
                      placeholder="e.g. 5"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2.5 text-xs focus:outline-none focus:border-[#4ADE80] placeholder:text-white/20" />
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button data-testid="driver-save-profile-btn"
                    onClick={handleSaveProfile} disabled={savingProfile}
                    className="flex items-center gap-2 px-5 py-2.5 font-head text-xs font-bold uppercase tracking-[0.15em] disabled:opacity-50 transition-all hover:brightness-110"
                    style={{ background: DRIVER_COLOR, color: DARK }}>
                    {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Save Changes
                  </button>
                  <button onClick={() => setShowEditProfile(false)} className="text-xs text-white/30 hover:text-white/60 transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Approved bids ── */}
          {profile.approved_bids?.length > 0 && (
            <div style={{ background: MID, border: `1px solid rgba(${DRIVER_GLOW},0.25)` }}>
              <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                <p className="font-head text-xs font-bold uppercase tracking-[0.2em]" style={{ color: DRIVER_COLOR }}>
                  Approved Offers — Contact Your Recruiter
                </p>
              </div>
              {profile.approved_bids.map((b) => (
                <div key={b.id} className="px-4 py-3 border-b border-white/05 last:border-0">
                  <p className="font-head text-sm font-bold text-white">{b.carrier_name}</p>
                  <p className="text-xs text-white/50 mt-0.5">
                    {b.position_type?.replace("_", " ")} · {b.equipment_package} · ${b.placement_fee?.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* ── Document uploads ── */}
          <div>
            <div className="mb-4">
              <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Your Documents</p>
              <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
                Upload Documents
              </h3>
              <p className="text-white/40 text-xs mt-1">
                MVR, PSP, and Clearinghouse docs increase your attractiveness to carriers. Complete all three to unlock the $65 bonus.
              </p>
            </div>

            {/* CDL Issue Date */}
            <div
              className="mb-4 p-4 flex flex-wrap items-center gap-4"
              style={{ background: MID, border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="flex-1 min-w-0">
                <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/50 mb-0.5">
                  CDL Issue Date
                </p>
                <p className="text-xs text-white/30">
                  Required to determine if a previous CDL upload is needed.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="date"
                  data-testid="driver-cdl-issue-date"
                  value={datesForm.cdl_issue_date}
                  onChange={(e) => setDatesForm((f) => ({ ...f, cdl_issue_date: e.target.value }))}
                  className="bg-[#0A0A0A] border border-white/10 text-white px-3 py-1.5 text-sm focus:outline-none focus:border-[#4ADE80]"
                />
                <button
                  disabled={savingDates || !datesForm.cdl_issue_date}
                  onClick={() => saveDates({ cdl_issue_date: datesForm.cdl_issue_date })}
                  data-testid="driver-save-cdl-date"
                  className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] disabled:opacity-40 transition-all"
                  style={{ background: DRIVER_COLOR, color: DARK }}
                >
                  {savingDates ? "…" : "Save"}
                </button>
              </div>
              {profile?.requires_previous_cdl && (
                <div
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs"
                  style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.25)", color: GOLD }}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  Your CDL was issued within the last 2 years — a previous CDL upload is required.
                </div>
              )}
            </div>

            <div className={`grid gap-4 ${DOC_CARDS.length === 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
              {DOC_CARDS.map(({ key, label, desc, has }) => (
                <div
                  key={key}
                  data-testid={`driver-doc-${key}`}
                  className="p-4 flex flex-col gap-3"
                  style={{
                    background: has ? `rgba(${DRIVER_GLOW},0.07)` : MID,
                    border: `1px solid rgba(${has ? DRIVER_GLOW + ",0.3" : "255,255,255,0.08"})`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-head text-sm font-bold uppercase tracking-[0.12em] text-white">{label}</p>
                      <p className="text-[10px] text-white/40 mt-0.5">{desc}</p>
                    </div>
                    <span
                      className="font-head text-[9px] font-black uppercase px-2 py-1"
                      style={{
                        background: has ? `rgba(${DRIVER_GLOW},0.15)` : "rgba(255,255,255,0.05)",
                        color: has ? DRIVER_COLOR : "#555",
                        border: `1px solid ${has ? `rgba(${DRIVER_GLOW},0.3)` : "#2a2a2a"}`,
                      }}
                    >
                      {has ? "✓ Uploaded" : "Missing"}
                    </span>
                  </div>
                  <input
                    type="file"
                    ref={fileRefs[key]}
                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                    className="hidden"
                    onChange={(e) => handleFileChange(key, e)}
                  />
                  <button
                    onClick={() => fileRefs[key].current?.click()}
                    disabled={uploading[key]}
                    data-testid={`driver-upload-${key}`}
                    className="w-full flex items-center justify-center gap-2 py-2.5 font-head text-[10px] uppercase tracking-[0.2em] border transition-all disabled:opacity-50"
                    style={{
                      borderColor: has ? `rgba(${DRIVER_GLOW},0.3)` : "rgba(255,255,255,0.15)",
                      color: has ? DRIVER_COLOR : "rgba(255,255,255,0.7)",
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = DRIVER_COLOR; e.currentTarget.style.color = DRIVER_COLOR; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = has ? `rgba(${DRIVER_GLOW},0.3)` : "rgba(255,255,255,0.15)"; e.currentTarget.style.color = has ? DRIVER_COLOR : "rgba(255,255,255,0.7)"; }}
                  >
                    {uploading[key] ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Paperclip className="w-3.5 h-3.5" />
                    )}
                    {has ? "Re-upload" : "Upload"} {label}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ── CDL Documents ── */}
          <div>
            <div className="mb-4 flex items-center gap-2">
              <div className="w-1 h-4" style={{ background: ACCENT }} />
              <h3 className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">CDL Documents</h3>
              <span className="font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5" style={{ background: "rgba(255,77,0,0.08)", color: "rgba(255,77,0,0.6)", border: "1px solid rgba(255,77,0,0.2)" }}>
                {[profile?.has_cdl, profile?.has_cdl_back, profile?.has_previous_cdl_1, profile?.has_previous_cdl_2].filter(Boolean).length}/4 Uploaded
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              {/* CDL Front — recruiter-managed, show status only */}
              <div
                className="p-4 flex flex-col gap-2"
                style={{
                  background: profile?.has_cdl ? `rgba(${DRIVER_GLOW},0.07)` : MID,
                  border: `1px solid rgba(${profile?.has_cdl ? DRIVER_GLOW + ",0.3" : "255,255,255,0.08"})`,
                }}
              >
                <div>
                  <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">CDL Front</p>
                  <p className="text-[10px] text-white/30 mt-0.5">Managed by recruiter</p>
                </div>
                <span className="font-head text-[9px] font-black uppercase px-2 py-1 self-start"
                  style={{ background: profile?.has_cdl ? `rgba(${DRIVER_GLOW},0.15)` : "rgba(255,255,255,0.05)", color: profile?.has_cdl ? DRIVER_COLOR : "#555", border: `1px solid ${profile?.has_cdl ? `rgba(${DRIVER_GLOW},0.3)` : "#2a2a2a"}` }}>
                  {profile?.has_cdl ? "✓ On File" : "Pending"}
                </span>
              </div>

              {/* Uploadable CDL Slots */}
              {CDL_SLOTS.map(({ key, label, desc, has }) => (
                <div key={key} data-testid={`driver-cdl-slot-${key}`}
                  className="p-4 flex flex-col gap-3"
                  style={{ background: has ? `rgba(${DRIVER_GLOW},0.07)` : MID, border: `1px solid rgba(${has ? DRIVER_GLOW + ",0.3" : "255,255,255,0.08"})` }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">{label}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">{desc}</p>
                    </div>
                    <span className="font-head text-[9px] font-black uppercase px-2 py-1"
                      style={{ background: has ? `rgba(${DRIVER_GLOW},0.15)` : "rgba(255,255,255,0.05)", color: has ? DRIVER_COLOR : "#555", border: `1px solid ${has ? `rgba(${DRIVER_GLOW},0.3)` : "#2a2a2a"}` }}>
                      {has ? "✓" : "—"}
                    </span>
                  </div>
                  <input type="file" ref={cdlSlotRefs[key]} accept=".pdf,.jpg,.jpeg,.png,.heic" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(key, f); e.target.value = ""; }} />
                  <button onClick={() => cdlSlotRefs[key].current?.click()} disabled={uploading[key]}
                    data-testid={`driver-upload-cdl-${key}`}
                    className="w-full flex items-center justify-center gap-2 py-2.5 font-head text-[10px] uppercase tracking-[0.18em] border transition-all disabled:opacity-50"
                    style={{ borderColor: has ? `rgba(${DRIVER_GLOW},0.3)` : "rgba(255,255,255,0.15)", color: has ? DRIVER_COLOR : "rgba(255,255,255,0.6)", background: "transparent" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = DRIVER_COLOR; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = has ? `rgba(${DRIVER_GLOW},0.3)` : "rgba(255,255,255,0.15)"; }}>
                    {uploading[key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    {has ? "Re-upload" : "Upload"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ── Milestone Dates ── */}
          <div>
            <div className="mb-4">
              <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Milestone Dates</p>
              <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
                Confirm Your Timeline
              </h3>
              <p className="text-white/40 text-xs mt-1">
                Setting your drug screen and start dates unlocks additional bonuses.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Drug screen date */}
              <div
                className="p-4"
                style={{
                  background: profile?.drug_screen_eligible_date ? "rgba(74,222,128,0.06)" : MID,
                  border: `1px solid ${profile?.drug_screen_eligible_date ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4" style={{ color: profile?.drug_screen_eligible_date ? DRIVER_COLOR : "#555" }} />
                  <div>
                    <p className="font-head text-xs font-bold uppercase tracking-[0.12em] text-white">Drug Screen Date</p>
                    <p className="text-[10px] text-white/40">When are you eligible for drug screening?</p>
                  </div>
                  {profile?.drug_screen_eligible_date && (
                    <span className="ml-auto font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                      style={{ background: "rgba(74,222,128,0.12)", color: DRIVER_COLOR, border: "1px solid rgba(74,222,128,0.3)" }}>
                      Set
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    data-testid="driver-drug-screen-date"
                    value={datesForm.drug_screen_eligible_date}
                    onChange={(e) => setDatesForm((f) => ({ ...f, drug_screen_eligible_date: e.target.value }))}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 text-white px-3 py-1.5 text-sm focus:outline-none focus:border-[#4ADE80]"
                  />
                  <button
                    disabled={savingDates || !datesForm.drug_screen_eligible_date}
                    onClick={() => saveDates({ drug_screen_eligible_date: datesForm.drug_screen_eligible_date })}
                    data-testid="driver-save-drug-screen"
                    className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] disabled:opacity-40 transition-all"
                    style={{ background: DRIVER_COLOR, color: DARK }}
                  >
                    {savingDates ? "…" : "Confirm"}
                  </button>
                </div>
              </div>
              {/* Start date */}
              <div
                className="p-4"
                style={{
                  background: profile?.start_eligible_date ? "rgba(74,222,128,0.06)" : MID,
                  border: `1px solid ${profile?.start_eligible_date ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4" style={{ color: profile?.start_eligible_date ? DRIVER_COLOR : "#555" }} />
                  <div>
                    <p className="font-head text-xs font-bold uppercase tracking-[0.12em] text-white">Start Date</p>
                    <p className="text-[10px] text-white/40">When can you begin a new position?</p>
                  </div>
                  {profile?.start_eligible_date && (
                    <span className="ml-auto font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                      style={{ background: "rgba(74,222,128,0.12)", color: DRIVER_COLOR, border: "1px solid rgba(74,222,128,0.3)" }}>
                      Set
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    data-testid="driver-start-date"
                    value={datesForm.start_eligible_date}
                    onChange={(e) => setDatesForm((f) => ({ ...f, start_eligible_date: e.target.value }))}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 text-white px-3 py-1.5 text-sm focus:outline-none focus:border-[#4ADE80]"
                  />
                  <button
                    disabled={savingDates || !datesForm.start_eligible_date}
                    onClick={() => saveDates({ start_eligible_date: datesForm.start_eligible_date })}
                    data-testid="driver-save-start-date"
                    className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] disabled:opacity-40 transition-all"
                    style={{ background: DRIVER_COLOR, color: DARK }}
                  >
                    {savingDates ? "…" : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Bonus Tracker ── */}
          <div>
            <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Driver Rewards</p>
                <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
                  Bonus Tracker
                </h3>
                <p className="text-white/40 text-xs mt-1">Complete milestones to earn cash bonuses. Admin approves each payout.</p>
              </div>
              {profile?.bonus_total > 0 && (
                <div
                  className="px-4 py-2 flex items-center gap-2"
                  style={{ background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)" }}
                >
                  <DollarSign className="w-4 h-4" style={{ color: GOLD }} />
                  <p className="font-head font-black text-lg" style={{ color: GOLD }}>
                    ${profile.bonus_total} <span className="text-sm font-normal text-white/40">earned</span>
                  </p>
                </div>
              )}
            </div>

            {/* ── Step 1: Pre-hire milestones ── */}
            <p className="font-head text-[8px] uppercase tracking-[0.3em] text-white/20 mb-2">Step 1 — Docs &amp; Readiness</p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {[
                {
                  amount: "$65",
                  label: "Documents Complete",
                  desc: profile?.requires_previous_cdl
                    ? "MVR + PSP + CLR + Previous CDL"
                    : "MVR + PSP + Clearinghouse all on file",
                  status: profile?.bonus_docs_status || "not_eligible",
                  testId: "bonus-docs",
                },
                {
                  amount: "$15",
                  label: "Drug Screen Date",
                  desc: profile?.drug_screen_eligible_date
                    ? `Set for ${profile.drug_screen_eligible_date}`
                    : "Set your drug screen date above",
                  status: profile?.bonus_drug_screen_status || "not_eligible",
                  testId: "bonus-drug-screen",
                },
                {
                  amount: "$20",
                  label: "Start Date Confirmed",
                  desc: profile?.start_eligible_date
                    ? `Ready ${profile.start_eligible_date}`
                    : "Set your start date above",
                  status: profile?.bonus_start_status || "not_eligible",
                  testId: "bonus-start",
                },
              ].map(({ amount, label, desc, status, testId }) => {
                const isApproved = status === "approved";
                const isPending  = status === "pending_approval";
                return (
                  <div
                    key={testId}
                    data-testid={`driver-${testId}`}
                    className="p-4 flex flex-col gap-2 relative overflow-hidden"
                    style={{
                      background: isApproved ? "rgba(74,222,128,0.08)" : isPending ? "rgba(255,77,0,0.06)" : MID,
                      border: `1px solid ${isApproved ? "rgba(74,222,128,0.35)" : isPending ? "rgba(255,77,0,0.3)" : "rgba(255,255,255,0.07)"}`,
                    }}
                  >
                    {isApproved && <div className="absolute top-0 right-0 w-12 h-12 opacity-10" style={{ background: `radial-gradient(circle, ${DRIVER_COLOR}, transparent)` }} />}
                    <div className="flex items-start justify-between">
                      <p className="font-head font-black text-3xl leading-none" style={{ color: isApproved ? DRIVER_COLOR : isPending ? ACCENT : "#333" }}>{amount}</p>
                      <span className="font-head text-[8px] font-black uppercase px-2 py-1 tracking-[0.1em]"
                        style={{ background: isApproved ? "rgba(74,222,128,0.15)" : isPending ? "rgba(255,77,0,0.12)" : "rgba(255,255,255,0.04)", color: isApproved ? DRIVER_COLOR : isPending ? ACCENT : "#444", border: `1px solid ${isApproved ? "rgba(74,222,128,0.3)" : isPending ? "rgba(255,77,0,0.25)" : "#1e1e1e"}` }}>
                        {isApproved ? "✓ Earned" : isPending ? "Pending Review" : "Not Yet"}
                      </span>
                    </div>
                    <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">{label}</p>
                    <p className="text-[10px] text-white/35 leading-relaxed">{desc}</p>
                  </div>
                );
              })}
            </div>

            {/* ── Step 2: Carrier hire plan bonuses (appear after transfer) ── */}
            {(() => {
              const planType = profile?.bonus_carrier_plan_type;
              const transferred = profile?.market_status === "transferred";
              if (!transferred && !planType) return (
                <div className="p-4 flex items-center gap-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                  <Award className="w-5 h-5 shrink-0 opacity-20" style={{ color: GOLD }} />
                  <div>
                    <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/25">Step 2 — After Carrier Hire</p>
                    <p className="text-[10px] text-white/20 mt-0.5">Once a carrier selects you, additional bonuses unlock here automatically.</p>
                  </div>
                </div>
              );

              const weeklyPrice = profile?.bonus_carrier_weekly_price || 0;
              const weeklyRoyalty = +(weeklyPrice * 0.10).toFixed(2);

              const carrierBonuses = [];

              if (planType === "weekly_flex") {
                carrierBonuses.push({
                  amount: `$${weeklyRoyalty}/wk`,
                  label: "Weekly Royalty",
                  desc: `10% of carrier's $${weeklyPrice}/wk subscription — paid weekly while active`,
                  status: profile?.bonus_carrier_start_status || "pending_approval",
                  testId: "bonus-carrier-weekly",
                  isRecurring: true,
                });
              } else if (planType === "standard") {
                carrierBonuses.push({
                  amount: "$50",
                  label: "Hire Bonus — Start",
                  desc: "Paid when you begin your first day with the carrier",
                  status: profile?.bonus_carrier_start_status || "pending_approval",
                  testId: "bonus-carrier-start",
                });
                carrierBonuses.push({
                  amount: "$50",
                  label: "Hire Bonus — 2 Weeks",
                  desc: "Paid after completing two weeks with the carrier",
                  status: profile?.bonus_carrier_week2_status || "pending_approval",
                  testId: "bonus-carrier-week2",
                });
              } else if (planType === "one_time_discount") {
                carrierBonuses.push({
                  amount: "$50",
                  label: "Hire Bonus — One-Time",
                  desc: "Single payment upon starting with the carrier",
                  status: profile?.bonus_carrier_onetime_status || "pending_approval",
                  testId: "bonus-carrier-onetime",
                });
              }

              // Mystery signup bonus — always after transfer (auto-approved on hire)
              carrierBonuses.push({
                amount: profile?.bonus_signup_amount ? `$${profile.bonus_signup_amount.toLocaleString()}` : "???",
                label: "Signup Bonus",
                desc: profile?.bonus_signup_status === "approved"
                  ? "Auto-approved when you were hired — contact your recruiter for payout"
                  : "Your carrier provides a signup bonus — amount revealed by the team",
                status: profile?.bonus_signup_status || "pending_approval",
                testId: "bonus-signup",
                isMystery: profile?.bonus_signup_status !== "approved",
              });

              // Parse offer-based bonuses from bid_message
              const bidMsg = profile?.active_bid?.bid_message || "";
              const offerParsed = {};
              bidMsg.split("\n").forEach(line => {
                const [k, ...v] = line.split(":");
                if (k && v.length) offerParsed[k.trim().toLowerCase()] = v.join(":").trim();
              });
              const offerBonusDefs = [
                { msgKey: "safety bonus",           label: "Safety Bonus",            testId: "bonus-safety",   desc: "Monthly safety incentive from your carrier" },
                { msgKey: "driver of the week",     label: "Driver of the Week",       testId: "bonus-dotw",     desc: "Weekly performance recognition bonus" },
                { msgKey: "driver of the month",    label: "Driver of the Month",      testId: "bonus-dotm",     desc: "Monthly top-performer bonus" },
              ];
              offerBonusDefs.forEach(({ msgKey, label, testId, desc }) => {
                const val = offerParsed[msgKey];
                if (val && val !== "$0") {
                  carrierBonuses.push({ amount: val, label, desc, status: "pending_approval", testId, isMystery: false });
                }
              });
              // Longevity bonus (always shown after hire as aspirational — countdown in dedicated section below)
              carrierBonuses.push({
                amount: "TBD",
                label: "Longevity Bonus",
                desc: "6-month & 1-year milestones — see countdown below",
                status: "not_started",
                testId: "bonus-longevity",
              });

              return (
                <>
                  <p className="font-head text-[8px] uppercase tracking-[0.3em] text-white/20 mb-2">Step 2 — After Carrier Hire</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {carrierBonuses.map(({ amount, label, desc, status, testId, isRecurring, isMystery }) => {
                      const isApproved = status === "approved";
                      const isPending  = status === "pending_approval";
                      return (
                        <div
                          key={testId}
                          data-testid={`driver-${testId}`}
                          className="p-4 flex flex-col gap-2 relative overflow-hidden"
                          style={{
                            background: isApproved ? "rgba(245,200,66,0.07)" : isPending ? "rgba(255,77,0,0.06)" : MID,
                            border: `1px solid ${isApproved ? "rgba(245,200,66,0.35)" : isPending ? "rgba(255,77,0,0.3)" : "rgba(255,255,255,0.07)"}`,
                          }}
                        >
                          {isApproved && <div className="absolute top-0 right-0 w-12 h-12 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD}, transparent)` }} />}
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-head font-black text-3xl leading-none"
                              style={{ color: isApproved ? GOLD : isPending ? ACCENT : "#333" }}>
                              {amount}
                            </p>
                            <div className="flex flex-col items-end gap-1">
                              {isRecurring && <span className="font-head text-[7px] uppercase tracking-[0.1em] px-1.5 py-0.5" style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>Recurring</span>}
                              {isMystery && !isApproved && <span className="font-head text-[7px] uppercase tracking-[0.1em] px-1.5 py-0.5" style={{ background: "rgba(255,255,255,0.04)", color: "#666", border: "1px solid #222" }}>Mystery</span>}
                              <span className="font-head text-[8px] font-black uppercase px-2 py-1 tracking-[0.1em]"
                                style={{ background: isApproved ? "rgba(245,200,66,0.15)" : isPending ? "rgba(255,77,0,0.12)" : "rgba(255,255,255,0.04)", color: isApproved ? GOLD : isPending ? ACCENT : "#444", border: `1px solid ${isApproved ? "rgba(245,200,66,0.3)" : isPending ? "rgba(255,77,0,0.25)" : "#1e1e1e"}` }}>
                                {isApproved ? "✓ Earned" : "Pending"}
                              </span>
                            </div>
                          </div>
                          <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">{label}</p>
                          <p className="text-[10px] text-white/35 leading-relaxed">{desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {/* ── Longevity Bonus Countdown ── */}
            {profile?.market_status === "transferred" && profile?.transferred_at && (() => {
              const hireDate  = new Date(profile.transferred_at);
              const now       = Date.now();
              const daysSince = Math.floor((now - hireDate.getTime()) / 86400000);
              const milestones = [
                { label: "6-Month Longevity",  days: 183, desc: "Reach 6 months with your carrier" },
                { label: "1-Year Longevity",   days: 365, desc: "Reach 1 full year — top milestone" },
              ];
              return (
                <div className="mt-4 space-y-3" data-testid="longevity-countdown">
                  <p className="font-head text-[8px] uppercase tracking-[0.3em] text-white/20">Longevity Milestones</p>
                  {milestones.map(({ label, days, desc }) => {
                    const daysLeft    = Math.max(0, days - daysSince);
                    const pct         = Math.min(100, Math.round((daysSince / days) * 100));
                    const reached     = daysSince >= days;
                    const targetDate  = new Date(hireDate.getTime() + days * 86400000)
                      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                    return (
                      <div key={label} className="p-4" style={{ background: reached ? "rgba(74,222,128,0.06)" : "rgba(245,200,66,0.03)", border: `1px solid ${reached ? "rgba(74,222,128,0.25)" : "rgba(245,200,66,0.12)"}` }}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div>
                            <p className="font-head text-xs font-bold text-white">{label}</p>
                            <p className="text-[10px] text-white/35 mt-0.5">{desc}</p>
                          </div>
                          {reached ? (
                            <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] shrink-0" style={{ background: "rgba(74,222,128,0.1)", color: DRIVER_COLOR, border: "1px solid rgba(74,222,128,0.3)" }}>
                              Reached
                            </span>
                          ) : (
                            <div className="text-right shrink-0">
                              <p className="font-head text-lg font-black leading-none" style={{ color: GOLD }}>{daysLeft}<span className="text-xs font-normal text-white/30 ml-1">days left</span></p>
                              <p className="text-[9px] text-white/25 mt-0.5">{targetDate}</p>
                            </div>
                          )}
                        </div>
                        {/* Progress bar */}
                        <div className="h-1 w-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                          <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: reached ? DRIVER_COLOR : GOLD }} />
                        </div>
                        <p className="text-[9px] text-white/20 mt-1">{pct}% complete · {daysSince} days active</p>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {profile?.bonus_carrier_plan_type === "weekly_flex" && profile?.market_status === "transferred" && (() => {
              const transferredAt = profile?.transferred_at;
              const weeksActive = transferredAt
                ? Math.max(0, Math.floor((Date.now() - new Date(transferredAt).getTime()) / (1000 * 60 * 60 * 24 * 7)))
                : null;
              const weeklyEarning = profile?.bonus_carrier_weekly_price ? +(profile.bonus_carrier_weekly_price * 0.10).toFixed(2) : 0;
              return (
                <div className="mt-4 p-4 space-y-3" style={{ background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.15)" }} data-testid="weekly-flex-tracker">
                  <p className="font-head text-[8px] uppercase tracking-[0.3em] text-white/20">Weekly Flex Tracker</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div>
                      <p className="font-head text-3xl font-black leading-none" style={{ color: GOLD }}>
                        {weeksActive !== null ? weeksActive : "—"}
                      </p>
                      <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/35 mt-1">Week{weeksActive !== 1 ? "s" : ""} Active</p>
                    </div>
                    {weeklyEarning > 0 && (
                      <div>
                        <p className="font-head text-2xl font-black leading-none text-white">${weeklyEarning}<span className="text-sm font-normal text-white/40">/wk</span></p>
                        <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/35 mt-1">Your Royalty</p>
                      </div>
                    )}
                    {weeksActive !== null && weeklyEarning > 0 && (
                      <div>
                        <p className="font-head text-2xl font-black leading-none" style={{ color: DRIVER_COLOR }}>${(weeksActive * weeklyEarning).toFixed(2)}</p>
                        <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/35 mt-1">Total Royalties</p>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-white/25 leading-relaxed">10% of the carrier's weekly subscription paid to you each week. Contact your recruiter to confirm weekly payouts.</p>
                </div>
              );
            })()}

            {/* ── Referral Bonus Tracker ── */}
            {/* ── Referral Program — big card + tracker ── */}
            <div className="mt-4" data-testid="referral-bonus-tracker">
              {/* Big referral card — links to Loyalty page */}
              <a
                href="/loyalty"
                className="block relative overflow-hidden group no-underline"
                style={{ background: "linear-gradient(135deg,#0e1a10 0%,#0c1c0d 100%)", border: "1px solid rgba(74,222,128,0.3)", textDecoration: "none" }}
                data-testid="referral-card-link"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "radial-gradient(ellipse at 60% 40%, rgba(74,222,128,0.08) 0%, transparent 70%)" }} />
                <div className="relative p-5 flex items-center gap-5 flex-wrap">
                  <div className="w-12 h-12 flex items-center justify-center shrink-0"
                    style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)" }}>
                    <Users className="w-6 h-6" style={{ color: DRIVER_COLOR }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: DRIVER_COLOR }}>Referral Program</p>
                    <p className="font-head text-xl font-black text-white leading-tight">Refer a Driver &mdash;{" "}
                      <span style={{ color: DRIVER_COLOR }}>Earn $400</span>
                    </p>
                    <p className="text-xs text-white/40 mt-1.5 leading-relaxed">Refer a CDL-A driver to Hylas. When they complete their first load, you earn <strong className="text-white">$400</strong>. Share your link below.</p>
                  </div>
                  <div className="flex items-center gap-1.5 font-head text-xs font-bold uppercase tracking-[0.15em] px-4 py-2.5"
                    style={{ background: DRIVER_COLOR, color: "#0A0A0A" }}>
                    View Program <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </div>
                </div>
              </a>

              {/* Referral link copy */}
              {referrals.referral_slug && (
                <>
                  <style>{`
                    @keyframes referralGlow {
                      0%, 100% { opacity: 0.65; text-shadow: 0 0 6px rgba(245,158,11,0.25); }
                      50%       { opacity: 1;    text-shadow: 0 0 14px rgba(245,158,11,0.55); }
                    }
                    @keyframes hubArrow {
                      0%, 100% { transform: translateX(0); }
                      50%       { transform: translateX(-4px); }
                    }
                  `}</style>
                  <div
                    className="mt-2 inline-flex items-center gap-2 self-start"
                    style={{
                      background: "rgba(245,158,11,0.04)",
                      border: "1px solid rgba(245,158,11,0.2)",
                      padding: "8px 12px",
                      maxWidth: "fit-content",
                    }}
                  >
                    <p
                      className="font-mono text-sm whitespace-nowrap"
                      data-testid="referral-link-text"
                      style={{ color: "#f59e0b", animation: "referralGlow 3s ease-in-out infinite" }}
                    >
                      {`hylascompanion.com/referral/${referrals.referral_slug}`}
                    </p>
                    <button
                      data-testid="referral-copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://hylascompanion.com/referral/${referrals.referral_slug}`);
                        toast.success("Referral link copied!");
                      }}
                      className="shrink-0 font-head text-[9px] uppercase tracking-[0.15em] px-2 py-1 transition-all hover:brightness-110"
                      style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#f59e0b" }}
                    >
                      Copy
                    </button>
                  </div>
                </>
              )}

              {/* Referred drivers tracker */}
              {referrals.referrals.length > 0 && (
                <div className="mt-2" style={{ border: "1px solid rgba(74,222,128,0.1)" }}>
                  <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: "rgba(74,222,128,0.05)", borderBottom: "1px solid rgba(74,222,128,0.1)" }}>
                    <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/40">Drivers You Referred</p>
                    <div className="flex gap-4">
                      {referrals.paid_earnings > 0 && <span className="font-head text-xs font-bold" style={{ color: DRIVER_COLOR }}>${referrals.paid_earnings} paid</span>}
                      {referrals.pending_earnings > 0 && <span className="font-head text-xs font-bold text-white/50">${referrals.pending_earnings} pending</span>}
                    </div>
                  </div>
                  {referrals.referrals.map((r) => {
                    const s = r.referral_bonus_paid ? { label: "$400 Paid", c: DRIVER_COLOR }
                      : r.market_status === "transferred" ? { label: "Hired — Bonus Pending", c: GOLD }
                      : r.market_status === "approved"    ? { label: "On Market", c: "#38BDF8" }
                      : { label: "Applied", c: "rgba(255,255,255,0.3)" };
                    return (
                      <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <div>
                          <p className="font-head text-xs font-bold text-white">{r.first_name} {r.last_name}</p>
                          <p className="text-[10px] text-white/30 mt-0.5">{r.state} · {r.cdl_class}</p>
                        </div>
                        <span className="font-head text-[9px] uppercase tracking-[0.1em] shrink-0" style={{ color: s.c }}>{s.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Chat with recruiters ── */}
          <div>
            <div className="mb-4">
              <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Direct Message</p>
              <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
                Chat with Recruiters
              </h3>
            </div>
            <ChatRoom room="drivers_recruiters" roomLabel="Drivers · Recruiters" token={token} hub="driver" />
          </div>
          </>)}
        </>
      )}
    </div>
  );
}

// ── MAIN MARKET PAGE ──────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export { DriversHub };
